import {
  DropdownType,
  EntityLookupSearchCriteria,
  LookupSearchCriteria,
  TableFilterApiBody,
  TableFilterLookupObjectBody,
} from '../types'
import {
  CaseStatusPayload,
  getEntityDataForQueueConfig,
  PayloadQueueConfig,
  PrintMailStatusAPIType,
  PrintMailVoidDiscardAPIType,
  QueueAssignmentType,
  QueueFilterApiBody,
} from '../types/queueCreation.types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT } from '../utils/environment'
import { validateApiParams } from '../utils/functions'

const ENTITY_ENDPOINT_QUEUE = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data`

export const createQueueApi = async (payload: PayloadQueueConfig) => {
  return await axiosInstance.post(`${API_END_POINT.CORE}v1/queues`, payload)
}

export const getAllQueuesApi = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}v1/queues`)
}

export const pageableQueuesApi = async (payload: {
  page?: number
  size?: number
  sort?: string
  filter?: TableFilterApiBody
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/queues/advance?sort=enabled,DESC`,
    payload?.filter,
    {
      params: {
        page: payload?.page,
        size: payload?.size ?? 50,
        sort: payload?.sort,
      },
    }
  )
}

export const deleteQueueByIdApi = async (id: number) => {
  return await axiosInstance.delete(`${API_END_POINT.CORE}v1/queues/${id}`)
}

export const getQueueByIdApi = async (id: number) => {
  validateApiParams(
    { id },
    { endpoint: `${API_END_POINT.CORE}v1/queues/${id}`, method: 'GET' }
  )
  return await axiosInstance.get(`${API_END_POINT.CORE}v1/queues/${id}`)
}

export const getCaseStatuses = async (payload: CaseStatusPayload) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/case_statuses`,
    payload
  )
}

export const getFilteredEntities = async (payload: QueueFilterApiBody) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER}entities/filters?includeSourceTypes=GLOBAL,SYSTEM,USER&size=1000`,
    payload
  )
}

export const updateQueueApi = async (payload: PayloadQueueConfig) => {
  return await axiosInstance.put(
    `${API_END_POINT.CORE}v1/queues/${payload?.id}`,
    payload
  )
}

export const getQueueLayoutData = async (
  id: number,
  payload: {
    filterCriteria:
      | {
          filter_criteria: LookupSearchCriteria[]
          related_entities: TableFilterLookupObjectBody
        }
      | {
          filter_criteria: EntityLookupSearchCriteria[]
          related_entities: TableFilterLookupObjectBody
        }
    size?: number
    page?: number
  }
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/queues/layout-record/${id}?size=${payload?.size}&page=${payload?.page}`,
    payload.filterCriteria
  )
}

export const getQueueLayoutDataForCase = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}users`)
}

export const toggleManageQueueAPI = async (id: number, enabled: boolean) => {
  return await axiosInstance.patch(
    `${API_END_POINT.CORE}v1/queues/${id}?enable=${!enabled}`
  )
}

export const getQueueAssignmentRolesAPI = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}roles`)
}

export const getQueueAssignmentAgentsAPI = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}users`)
}

export const addQueueAssignmentAPI = async (payload: QueueAssignmentType) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/queue-agents`,
    payload
  )
}

export const updateQueueAssignmentAPI = async (
  payload: QueueAssignmentType
) => {
  return await axiosInstance.put(
    `${API_END_POINT.CORE}v1/queue-agents`,
    payload
  )
}

export const geteQueueAssignmentByIdAPI = async (id: number) => {
  return await axiosInstance.get(`${API_END_POINT.CORE}v1/queue-agents/${id}`)
}

export const getQueueAssignmentByQueueIdApi = async (id: number) => {
  return await axiosInstance.get(
    `${API_END_POINT.CORE}v1/queue-agents/queue/${id}`
  )
}

export const searchURLManageQueue = `${API_END_POINT.CORE}dropdown/filter-data/QUEUE/`

export const getOldestWorkForAgentAPI = async (
  queueId: number,
  userId: number,
  serviceNames: string[],
  programServices: {
    [key: string]: number[]
  }
) => {
  return await axiosInstance.patch(
    `${
      API_END_POINT.CORE
    }queueAgent/assign?queueId=${queueId}&userId=${userId}&serviceNames=${serviceNames.join(
      ','
    )}`,
    programServices
  )
}

export const assignWorkToAgentAPI = async (
  rowIds: string[],
  agent: DropdownType<string> | null,
  layoutId: string | undefined,
  entityId: string | undefined
) => {
  return await axiosInstance.patch(
    `${API_END_POINT.V3_ENTITY_MANAGER}entity-data/entities?ids=${rowIds}`,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: {
        users: {
          id: agent?.['id'],
        },
        assigned_to: agent?.label,
      },
    }
  )
}

export const getEntityDataByEntityIdLayoutIdApiForFilterQueue = async (
  action: getEntityDataForQueueConfig
) => {
  return await axiosInstance.post(
    `${ENTITY_ENDPOINT_QUEUE}/get/${action?.entityName}`,
    action?.payload
  )
}

export const getPermissionSetsAPI = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}permission-set`)
}

export const getProgramForQueueConfigAPI = async () => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}programs/filter/advance`,
    {
      fields: null,
      search_criteria: [
        {
          search_key: 'ENABLED',
          operator: 'CONTAINS',
          search_value: [true],
        },
      ],
    },
    {
      params: {
        page: 0,
        size: 1000,
      },
    }
  )
}

export const createGroupQueueConfigAPI = async (payload: { name: string }) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/queuesAssignGroup`,
    payload
  )
}

export const getAssignedGroupsQueueConfigAPI = async () => {
  return await axiosInstance.get(`${API_END_POINT.CORE}v1/queuesAssignGroup`)
}

export const getProgramWiseAgentsAPI = async (
  programServiceIds: { [key: string]: string[] },
  permissionFlag: boolean
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}users/assign-to-agent?isPermissionFlag=${permissionFlag}`,
    programServiceIds
  )
}

export const updateStatusPrintMailAPI = async (
  recordIds: string,
  payload: PrintMailStatusAPIType
) => {
  return await axiosInstance.patch(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/entities?ids=${recordIds}`,
    payload
  )
}
export const bulkDiscardVoidPrintLetter = async (
  payload: PrintMailVoidDiscardAPIType
) => {
  return await axiosInstance.patch(
    `${API_END_POINT.CORE}v1/status/bulk_discard`,
    payload
  )
}

export const generateCommuncationStatusesAPI = () => {
  return axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}category-value/type?categoryType=communication_status`
  )
}
